import { uniq } from 'lodash';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';

import { Enum } from '~/data/enums';
import { CompensationEqualityReportData } from '~/data/reports';

export type ColumnFilterProps = {
  value: string;
  setValue: Dispatch<SetStateAction<string>>;
  options: Enum[];
};

type UseFilterCompensationEqualityRowsBySelectorOutput = ColumnFilterProps & {
  rows: CompensationEqualityReportData[];
};

type RowFilterProps = {
  defaultValue: string;
  filterEnums: Enum[];
  getFilterRowValue: (row: CompensationEqualityReportData) => string;
  rows: CompensationEqualityReportData[];
};

export const useFilterCompensationEqualityRowsBySelector = (
  props: RowFilterProps,
): UseFilterCompensationEqualityRowsBySelectorOutput => {
  const { defaultValue, filterEnums, getFilterRowValue, rows } = props;

  const [filterSelected, setFilterSelected] = useState<string>(defaultValue);

  const { reportOptions, filteredRows } = useMemo(() => {
    const reportValues = uniq(rows.map(getFilterRowValue)).filter(
      (value) => !!value,
    );

    const reportOptions = filterEnums.filter(({ code }) =>
      reportValues.includes(code),
    );

    const filteredRows = rows.filter(
      (row) => getFilterRowValue(row) === filterSelected,
    );

    return {
      reportOptions,
      filteredRows,
    };
  }, [rows, getFilterRowValue, filterEnums, filterSelected]);

  useEffect(() => {
    const filterCodes = reportOptions.map((e) => e.code);

    if (
      reportOptions.length > 0 &&
      (!filterSelected || !filterCodes.includes(filterSelected))
    ) {
      setFilterSelected(reportOptions[0].code);
    }
  }, [reportOptions, filterSelected, setFilterSelected]);

  return {
    rows: filteredRows,
    value: filterSelected,
    setValue: setFilterSelected,
    options: reportOptions,
  };
};
